import { Point, Polygon } from "geojson";

/**
 * Get padding for a map center based on the container size.
 * @param percentage The percentage of the container size to use as padding.
 */
export function getDynamicMapPadding(
	map: mapboxgl.Map,
	percentage = 0.15,
): number {
	const container = map.getContainer();
	const padding = container.clientWidth * percentage;
	return padding;
}

/**
 * Convert coordinates to GeoJSON format.
 * If only one coordinate is provided, it creates a Point.
 * If multiple coordinates are provided, it creates a Polygon.
 * @param coords The coordinates to convert, in the format [[lng, lat], ...].
 * @returns A GeoJSON Point or Polygon, or null if no coordinates are provided.
 */
export const coordsToGeoJSON = (coords: number[][]): Point | Polygon | null => {
	if (!coords || coords.length === 0) {
		return null;
	}

	if (coords.length === 1) {
		// Single coordinate - create a Point
		return {
			type: "Point",
			coordinates: coords[0],
		};
	} else {
		// Multiple coordinates - create a Polygon
		// Make sure the polygon is closed (first and last points are the same)
		const polygonCoords = [...coords];
		const firstPoint = coords[0];
		const lastPoint = coords[coords.length - 1];

		// Check if polygon is already closed
		if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
			// Close the polygon by adding the first point again at the end
			polygonCoords.push(firstPoint);
		}

		return {
			type: "Polygon",
			coordinates: [polygonCoords], // Note the extra array nesting - GeoJSON requires this
		};
	}
};

/**
 * Convert a GeoJSON Point or Polygon to a human-readable string.
 * For Points, it returns the latitude and longitude.
 * For Polygons, it returns the number of points in the polygon.
 * @param location The GeoJSON Point or Polygon to convert.
 * @returns A string representation of the location.
 */
export const formatHumanReadableGeoJSON = (
	location: Point | Polygon,
): string => {
	if (location.type === "Point") {
		const [lng, lat] = location.coordinates;
		return `${lat.toFixed(6)}° N, ${lng.toFixed(6)}° E`;
	}
	const pointCount = location.coordinates[0].length - 1;
	return `Area with ${pointCount} points`;
};
